import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { F0Box } from '@factorialco/f0-react'
import {
  IncentivesCompensationPlanDetailDocument,
  IncentivesCompensationPlanDetailRouteDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { CompensationPlanAssignments } from './CompensationPlanAssignments'
import { CompensationPlanMetrics } from './CompensationPlanMetrics'
import { SendToCompensationDialog } from 'modules/incentives/components/SendToCompensationDialog'
import { DeleteCompensationPlanDialog } from 'modules/incentives/components/DeleteCompensationPlanDialog'
import { SideContent } from './SideContent'
import { type DetailTab, Tabs } from './Tabs'
import {
  getCurrentPeriod,
  getFirstNotExecutedPeriod,
  periodLabel,
} from 'modules/incentives/lib/period'
import { CompensationPlanDetailWidgets } from './Widgets'

export default function CompensationPlanDetail() {
  const { id } = useStrictParams<'id'>()
  const { company } = useCurrentSession()
  const apolloClient = useApolloClient()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const sendToCompensationParam = searchParams.get('sendToCompensation')
  const deletePlanParam = searchParams.get('deletePlan')
  const periodIdParam = searchParams.get('periodId')

  const [activeTab, setActiveTab] = useState<DetailTab>('metrics')

  const { data, loading, error } = useQuery(IncentivesCompensationPlanDetailDocument, {
    variables: { companyId: company.id, planId: String(id) },
  })

  const plan = data?.incentives?.compensationPlan
  const periods = plan?.compensationPlanPeriods ?? []
  const employeeCount = plan?.compensationPlanAssignmentsConnection.totalCount ?? 0

  const selectedPeriod =
    periods.find((p) => p.id === periodIdParam) ??
    getFirstNotExecutedPeriod(periods) ??
    getCurrentPeriod(periods)

  const selectedPeriodLabel =
    plan && selectedPeriod ? periodLabel(plan.recurrence, selectedPeriod) : ''

  const canCreateAssignment = plan?._policies?.createAssignment?.can ?? false
  const canCreateMetric = selectedPeriod?._policies?.createMetric?.can ?? false
  const canSendToCompensation = selectedPeriod?._policies?.createExecution?.can ?? false
  const canDeletePlan = plan?._policies?.delete?.can ?? false

  const closeDialogs = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('deletePlan')
    params.delete('sendToCompensation')
    setSearchParams(params)
  }

  useOneMutationSubscription('UpdateIncentivePlan', async () => {
    await apolloClient.refetchQueries({
      include: [
        IncentivesCompensationPlanDetailDocument,
        IncentivesCompensationPlanDetailRouteDocument,
      ],
    })
  })

  useOneMutationSubscription('DeleteIncentivePlan', () => {
    navigate(navigation.compensations.incentives.plans.fullPath())
  })

  return (
    <ResponsivePage.Body loading={loading} error={error} variant="fullscreen">
      {selectedPeriod && canSendToCompensation && (
        <SendToCompensationDialog
          isOpen={sendToCompensationParam === 'true'}
          onClose={closeDialogs}
          periodId={selectedPeriod.id}
          employeeCount={employeeCount}
          periodLabel={selectedPeriodLabel}
        />
      )}
      {plan && canDeletePlan && (
        <DeleteCompensationPlanDialog
          isOpen={deletePlanParam === 'true'}
          onClose={closeDialogs}
          planId={String(id)}
          onSuccess={() => navigate(navigation.compensations.incentives.plans.fullPath())}
        />
      )}
      {plan && selectedPeriod && (
        <F0Box display="flex" flexDirection="row" height="full" maxWidth="full">
          <F0Box
            display="flex"
            width="56"
            shrink={false}
            paddingY="xs"
            paddingX="lg"
            borderTop="default"
            borderColor="secondary"
            sm={{ paddingBottom: 'xl' }}
            md={{ paddingLeft: 'sm' }}
          >
            <SideContent
              periods={periods}
              recurrence={plan.recurrence}
              currentPeriodId={getCurrentPeriod(periods)?.id}
              selectedPeriodId={selectedPeriod.id}
              onPeriodSelect={(periodId) => setSearchParams({ periodId })}
            />
          </F0Box>
          <F0Box
            borderTop="default"
            grow
            minWidth="0"
            height="fit"
            minHeight="full"
            paddingY="xl"
            borderLeft="default"
            borderColor="secondary"
          >
            <CompensationPlanDetailWidgets period={selectedPeriod} />
            <F0Box paddingTop="2xl">
              <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
            </F0Box>
            <F0Box paddingTop="lg">
              {activeTab === 'metrics' ? (
                <CompensationPlanMetrics
                  period={selectedPeriod}
                  planName={plan.name}
                  periodLabel={selectedPeriodLabel}
                  canCreateMetric={canCreateMetric}
                />
              ) : (
                <CompensationPlanAssignments
                  periodId={selectedPeriod.id}
                  planName={plan.name}
                  canCreateAssignment={canCreateAssignment}
                />
              )}
            </F0Box>
          </F0Box>
        </F0Box>
      )}
    </ResponsivePage.Body>
  )
}
